import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoadingSpinner } from '../common/LoadingSpinner';
import type { UserRoleName } from '../../types/auth';

interface RoleProtectedRouteProps {
  allowedRoles: UserRoleName[];
  children?: React.ReactNode;
}

// Extends ProtectedRoute with an allow-list of roles. Non-matching roles are
// redirected to /forbidden (FR-005). Unauthenticated users hit /login with
// state.from preserved so post-login they land back here.
// Introduced by feature 009-overcalling-data-editing.
export function RoleProtectedRoute({ allowedRoles, children }: RoleProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/forbidden" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
